import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConfig } from '../../hooks/useAI';
import { apiUrl } from '../../lib/api-url';

const workflowTypes = [
  { type: 'onboarding', name: 'Onboarding Sequence', description: 'Welcome emails for new subscribers', icon: '👋' },
  { type: 'abandoned_cart', name: 'Abandoned Cart', description: 'Recover lost sales', icon: '🛒' },
  { type: 'reengagement', name: 'Re-engagement', description: 'Win back inactive subscribers', icon: '🔄' },
  { type: 'promotional', name: 'Promotional Campaign', description: 'Multi-step promo sequence', icon: '📢' },
  { type: 'newsletter', name: 'Weekly Newsletter', description: 'Automated newsletter delivery', icon: '📰' },
  { type: 'winback', name: 'Win-Back', description: 'Re-activate churned customers', icon: '💪' },
];

export default function AIWorkflowsPage() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [generatedWorkflow, setGeneratedWorkflow] = useState<any>(null);
  const [generating, setGenerating] = useState(false);

  const { data: config } = useQuery({ queryKey: ['ai-config'], queryFn: getConfig });

  const generateMutation = useMutation({
    mutationFn: async (data: { blueprint_type: string; description: string }) => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/workflow/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || json.error);
      return json.data;
    },
    onSuccess: (data) => {
      setGeneratedWorkflow(data);
      setGenerating(false);
    },
    onError: () => setGenerating(false),
  });

  const applyMutation = useMutation({
    mutationFn: async (blueprintId: string) => {
      const token = localStorage.getItem('access_token');
      const res = await fetch(apiUrl('/api/v1/ai/workflow/apply'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ blueprint_id: blueprintId }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || json.error);
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-workflows'] });
    },
  });

  const handleGenerate = () => {
    if (!selectedType) return;
    setGenerating(true);
    generateMutation.mutate({ blueprint_type: selectedType, description });
  };

  const handleApply = () => {
    if (generatedWorkflow?.blueprint?.id) {
      applyMutation.mutate(generatedWorkflow.blueprint.id);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">AI Workflow Builder</h2>
        <p className="text-gray-500 mt-1">Generate complete email automation workflows with AI.</p>
      </div>

      {/* Workflow Type Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose a Workflow Type</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {workflowTypes.map((wt) => (
            <button
              key={wt.type}
              onClick={() => setSelectedType(wt.type)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                selectedType === wt.type
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-2xl">{wt.icon}</span>
              <h4 className="font-semibold text-gray-900 mt-2">{wt.name}</h4>
              <p className="text-sm text-gray-500 mt-1">{wt.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Description */}
      {selectedType && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize (Optional)</h3>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            rows={3}
            placeholder="Describe your specific needs (e.g., 'For a SaaS product targeting small businesses')"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="mt-4 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Generate Workflow'}
          </button>
        </div>
      )}

      {/* Generated Workflow Preview */}
      {generatedWorkflow && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {generatedWorkflow.workflow_json?.name || 'Generated Workflow'}
            </h3>
            <button
              onClick={handleApply}
              disabled={applyMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {applyMutation.isPending ? 'Applying...' : 'Apply to Workflows'}
            </button>
          </div>

          {generatedWorkflow.workflow_json?.nodes && (
            <div className="space-y-3">
              {generatedWorkflow.workflow_json.nodes.map((node: any, i: number) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600 flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-lg px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{node.name}</p>
                    <p className="text-xs text-gray-500">{node.type}</p>
                  </div>
                  {node.delay_minutes > 0 && (
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      Wait {node.delay_minutes >= 1440 ? `${node.delay_minutes / 1440}d` : `${node.delay_minutes / 60}h`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {generatedWorkflow.workflow_json?.description && (
            <p className="text-sm text-gray-500 mt-4">{generatedWorkflow.workflow_json.description}</p>
          )}
        </div>
      )}
    </div>
  );
}
